import { Menu, UnstyledButton } from "@mantine/core";
import { Check, ChevronDown, X } from "lucide-react";
import { useState, type ReactNode } from "react";

import classes from "./FilterMenu.module.css";
import { Soon } from "./Soon";

export interface FilterOption<T extends string> {
  value: T;
  label: string;
}

/** A filter live has and the list route does not take yet: the chip alone, "Coming soon". */
export function SoonFilter({ label, icon }: { label: string; icon: ReactNode }) {
  return (
    <Soon>
      {(soon) => (
        <UnstyledButton {...soon} className={classes.chip}>
          <span className={classes.slot} aria-hidden>
            {icon}
          </span>
          <span className={classes.target}>
            {label}
            <ChevronDown size={16} aria-hidden />
          </span>
        </UnstyledButton>
      )}
    </Soon>
  );
}

export interface FilterMenuProps<T extends string> {
  /** The chip's word, which stays when a value is chosen ("Media"). */
  label: string;
  /** A 16px icon before the label, while nothing is chosen. */
  icon: ReactNode;
  options: readonly FilterOption<T>[];
  value: T | undefined;
  /** The chosen value, or undefined when the x clears it. */
  onChange: (value: T | undefined) => void;
}

/**
 * Live's filter chip over a gallery (`filter-select`, `clearable-target`; captures gaps/22 and
 * 23): the icon, the label and a chevron, styled as a type tab. It opens its options, the chosen
 * one checked; once one is chosen the chip is active and its icon becomes an x ("Clear <label>").
 */
export function FilterMenu<T extends string>(props: FilterMenuProps<T>) {
  const { label, icon, options, value, onChange } = props;
  const [opened, setOpened] = useState(false);
  return (
    <div className={classes.chip} data-active={value !== undefined || opened || undefined}>
      {value === undefined ? (
        <span className={classes.slot} aria-hidden>
          {icon}
        </span>
      ) : (
        <UnstyledButton
          className={classes.slot}
          aria-label={`Clear ${label}`}
          onClick={() => onChange(undefined)}
        >
          <X size={16} />
        </UnstyledButton>
      )}
      <Menu opened={opened} onChange={setOpened} position="bottom-start">
        <Menu.Target>
          <UnstyledButton className={classes.target}>
            {label}
            <ChevronDown size={16} className={classes.arrow} data-opened={opened || undefined} />
          </UnstyledButton>
        </Menu.Target>
        <Menu.Dropdown className={classes.dropdown}>
          {options.map((option) => (
            <Menu.Item
              key={option.value}
              rightSection={option.value === value ? <Check size={16} /> : null}
              onClick={() => onChange(option.value)}
            >
              {option.label}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
    </div>
  );
}
